// Where the stage gets what it shows. Most kinds read the app's own API (api.project, versions, estimate, library,
// models, formats); collections and characters read the v4 routes (V4-H1 GET /api/collections/:id, V4-C1
// GET /api/characters/:id) until the web gets clients of its own for them (V4-H2, V4-C4). The mock replaces the
// whole source (installStageSource), so the stage can be screenshotted with no backend.
import { useEffect, useState } from "react";
import { api, call } from "../api";
import type { FormatInfo } from "../formats";
import { useRefresh } from "../store";
import type { Asset, Estimate, LibraryScope, ModelInfo, Project, Version } from "../types";

/** A collection as the stage shows it. */
export type StageCollection = { id: string; name: string; kind: string; format: string | null; videos: number; needsYou: number; thumbUrl: string | null };
/** One sheet version: its panels by id (front, happy, walking...) and its three part pictures. */
export type StageSheetVersion = { n: number; status: string; approved: boolean; panels: Record<string, string | null>; sheets: Record<string, string | null>; createdAt: string | null };
/** A character as the stage shows it. */
export type StageCharacter = { id: string; name: string; look: string; state: string; voiceName: string | null; approved: number | null; versions: StageSheetVersion[] };

export type StageSource = {
  project(id: string): Promise<Project>;
  versions(projectId: string): Promise<Version[]>;
  estimate(projectId: string): Promise<Estimate>;
  library(scope: LibraryScope): Promise<Asset[]>;
  models(step?: string): Promise<ModelInfo[]>;
  formats(): Promise<FormatInfo[]>;
  collection(id: string): Promise<StageCollection>;
  character(id: string): Promise<StageCharacter>;
};

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);
const s = (v: unknown) => (typeof v === "string" ? v : "");
const n = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : 0);
const urls = (v: unknown): Record<string, string | null> =>
  isObj(v) ? Object.fromEntries(Object.entries(v).map(([k, r]) => [k, isObj(r) && typeof r.url === "string" ? r.url : typeof r === "string" ? r : null])) : {};

/** GET /api/collections/:id (V4-H1), loosely: `{collection: {...}}` or the collection itself. */
export function normCollection(v: unknown): StageCollection {
  const o = isObj(v) && isObj(v.collection) ? v.collection : isObj(v) ? v : {};
  const videos = Array.isArray(o.videos) ? o.videos.length : n(o.videos ?? o.videoCount ?? o.video_count);
  return { id: s(o.id), name: s(o.name) || "Collection", kind: s(o.kind) || "series", format: s(o.format) || null, videos, needsYou: n(o.needsYou ?? o.needs_you), thumbUrl: s(o.thumbUrl ?? o.thumb_url) || null };
}

/** A sheet version from V4-C1's VersionView. */
export function normSheetVersion(v: unknown): StageSheetVersion {
  const o = isObj(v) ? v : {};
  return { n: n(o.n), status: s(o.status) || "draft", approved: s(o.status) === "approved", panels: urls(o.panels), sheets: urls(o.sheets), createdAt: s(o.createdAt ?? o.created_at) || null };
}

/** GET /api/characters/:id (V4-C1): `{character: CharacterView, versions: VersionView[]}`. */
export function normCharacter(v: unknown): StageCharacter {
  const o = isObj(v) ? v : {};
  const c = isObj(o.character) ? o.character : o;
  const approved = isObj(c.approved) ? n(c.approved.n) || null : null;
  const versions = (Array.isArray(o.versions) ? o.versions : [c.latest, c.approved].filter(isObj)).map(normSheetVersion);
  const seen = new Set<number>();
  return {
    id: s(c.id),
    name: s(c.name) || "Character",
    look: s(c.look),
    state: s(c.state) || "none",
    voiceName: s(c.voiceName) || null,
    approved,
    versions: versions.filter((x) => x.n > 0 && !seen.has(x.n) && seen.add(x.n)).sort((a, b) => b.n - a.n),
  };
}

/** A library level as the library API filters it (a collection filter arrives with V4-A2; until then: everything). */
export function libraryQuery(scope: LibraryScope): { channelId?: string; projectId?: string } {
  const [level, id] = scope.split(":") as [string, string | undefined];
  if (level === "channel" && id) return { channelId: id };
  if (level === "project" && id) return { projectId: id };
  return {};
}

const httpSource: StageSource = {
  project: (id) => api.project(id),
  versions: (id) => api.versions(id),
  estimate: (id) => api.estimate(id),
  library: (scope) => api.library(libraryQuery(scope)),
  models: (step) => api.models(step && step !== "all" ? (step as Parameters<typeof api.models>[0]) : undefined),
  formats: () => api.formats(),
  collection: async (id) => normCollection(await call("GET", `collections/${encodeURIComponent(id)}`)),
  character: async (id) => normCharacter(await call("GET", `characters/${encodeURIComponent(id)}`)),
};

let source: StageSource = httpSource;
/** The mock (VITE_MOCK=1) installs its own source; tests can too. */
export function installStageSource(patch: Partial<StageSource>) {
  source = { ...source, ...patch };
}
export const stageSource = () => source;

export type Load<T> = { data: T | null; error: string | null; loading: boolean };

/**
 * Load what one stage item needs. `key` names it ("project:p1"); the result is cached for the visit, and the
 * project/library refresh signals reload it.
 */
const cache = new Map<string, unknown>();
export function useStageLoad<T>(key: string | null, load: (src: StageSource) => Promise<T>): Load<T> {
  const tick = useRefresh("project", "projects", "library", "characters", "collections");
  const [state, setState] = useState<Load<T>>(() => ({ data: key && cache.has(key) ? (cache.get(key) as T) : null, error: null, loading: !!key && !cache.has(key) }));
  useEffect(() => {
    if (!key) return;
    let live = true;
    setState((st) => ({ data: cache.has(key) ? (cache.get(key) as T) : st.data, error: null, loading: !cache.has(key) }));
    load(source)
      .then((data) => {
        cache.set(key, data);
        if (live) setState({ data, error: null, loading: false });
      })
      .catch((e: unknown) => live && setState({ data: null, error: e instanceof Error ? e.message : "Couldn't load it", loading: false }));
    return () => {
      live = false;
    };
  }, [key, tick]); // eslint-disable-line react-hooks/exhaustive-deps
  return state;
}
